import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { QueryHookOptions, useApolloClient, useLazyQuery, useQuery } from '@apollo/client'
import { Box, Spinner, useToggle } from '@factorialco/deprecated-design-system'
import { PayrollResultFragment } from 'generated/resources/graphql'
import {
  AvailablePayrollConceptFieldFragment,
  CompensationsListFragment,
  CompensationsPayrollResultsDocument,
  CompensationsPayrollRunDocument,
  CompensationsPayrollRunQuery,
  CompensationsPayrollRunQueryVariables,
  CyclesManagementCheckCompensationsPayrollRunDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { EmployeePayrollRunNode } from 'modules/compensations/components/EmployeePayrollRunElements'
// Import as type to avoid loading dependencies during tests
import type ImportPayrollResultModal from 'modules/compensations/components/ImportPayrollResultModal'
import { PayrollRunActions } from 'modules/compensations/components/PayrollRunActions'
import { CompensationsRefreshContext } from 'modules/compensations/lib/CompensationsRefreshContext'
import { useCurrencyContext } from 'modules/compensations/lib/CurrencyContext'
import { usePayrollRuns } from 'modules/compensations/lib/PayrollRunsContext'
import {
  EUR,
  PayrollRunStatusEnum,
  getCurrencyByIsoCode,
} from 'modules/compensations/lib/constants'
import { CompensationsExportAlertModal } from 'modules/compensations/pages/CompensationsPayrollRun/CompensationsExportAlertModal'
import { CompensationsList } from 'modules/compensations/pages/CompensationsPayrollRun/CompensationsList'
import { CycleCompensationsList } from 'modules/compensations/pages/CompensationsPayrollRun/CycleCompensationsList'
import { EditCompensationsFormStandalone } from 'modules/compensations/pages/CompensationsPayrollRun/EditCompensationsForm'
import LockToggleButton from 'modules/compensations/pages/CompensationsPayrollRun/LockToggleButton'
import { PayrollRunEmptyStateCallout } from 'modules/compensations/pages/CompensationsPayrollRun/PayrollRunEmptyStateCallout'
import PayrollRunStatusAlert from 'modules/compensations/pages/CompensationsPayrollRun/PayrollRunStatusAlert'
import TogglePayrollRunLockModal from 'modules/compensations/pages/CompensationsPayrollRun/TogglePayrollRunLockModal'
import { useCrudDisclosure } from 'modules/contracts/hooks/useCrudDisclosure'
import { SyncFooter } from 'modules/integrations/components/SyncFooter'
import { useIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'
import { DEFAULT_RESULT_CONCEPT } from 'modules/payroll_bundle/lib/constants'
import { ExportSidepanel } from 'modules/universal_exports/components/ExportSidepanel'

import ManagePayrollResultsModal from './ManagePayrollResultsModal'
import PayrollRunDataUpdatedInfo from './PayrollRunDataUpdatedInfo'

function useCompensationsPayrollRunQuery(
  baseOptions: QueryHookOptions<CompensationsPayrollRunQuery, CompensationsPayrollRunQueryVariables>
) {
  const options = { ...baseOptions }
  return useQuery<CompensationsPayrollRunQuery, CompensationsPayrollRunQueryVariables>(
    CompensationsPayrollRunDocument,
    options
  )
}

/**
 * Hook to handle currency updates based on legal entity settings
 */
function useCurrencyUpdate({
  mainLegalEntityCurrencySymbol,
  legalEntityCurrencySymbol,
  isCyclesManagementFeatureEnabled,
}: {
  mainLegalEntityCurrencySymbol: string | undefined
  legalEntityCurrencySymbol: string | undefined
  isCyclesManagementFeatureEnabled: boolean
}) {
  const [currency, setCurrency] = useCurrencyContext()

  React.useEffect(() => {
    if (
      !isCyclesManagementFeatureEnabled &&
      mainLegalEntityCurrencySymbol !== undefined &&
      mainLegalEntityCurrencySymbol !== currency.isoCode &&
      mainLegalEntityCurrencySymbol !== currency.symbol
    ) {
      setCurrency(getCurrencyByIsoCode(mainLegalEntityCurrencySymbol))
    } else if (isCyclesManagementFeatureEnabled && legalEntityCurrencySymbol) {
      setCurrency(getCurrencyByIsoCode(legalEntityCurrencySymbol))
    }
  }, [
    mainLegalEntityCurrencySymbol,
    isCyclesManagementFeatureEnabled,
    legalEntityCurrencySymbol,
    currency.isoCode,
    currency.symbol,
    setCurrency,
  ])

  return null
}

type CompensationsPayrollRunLayoutProps = {
  children: React.ReactNode
  onActionClick?: (key: string) => void
}

const CompensationsPayrollRunLayout = ({
  children,
  onActionClick,
}: CompensationsPayrollRunLayoutProps) => {
  const { syncEnabled } = useIntegrationsSyncContext()
  return (
    <ResponsivePage.Body height="none" onActionClick={onActionClick}>
      <Box
        gap="s16"
        height="full"
        style={{
          paddingBottom: syncEnabled ? '58px' : '29px',
        }}
      >
        {children}
      </Box>
    </ResponsivePage.Body>
  )
}

function CompensationsPayrollRun() {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const client = useApolloClient()
  const { id } = useParams()

  const {
    access: { locale },
    company,
  } = useCurrentSession()
  const { currentPayrollRun, loading: payrollRunsLoading } = usePayrollRuns()
  const { setContextParameters } = useIntegrationsSyncContext()

  const {
    data: featuresData,
    loading: loadingFeatures,
    error: errorFeatures,
  } = useQuery(CyclesManagementCheckCompensationsPayrollRunDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const isCyclesManagementFeatureEnabled =
    featuresData?.features?.featuresConnection?.nodes[0]?.enabled ?? false

  // TODO: we should cache the result of this query since this is gonna be used on CompensationsList
  // We will tackle this in a future PR
  const queryVariables =
    isCyclesManagementFeatureEnabled && id
      ? {
          companyId: company.id,
          id: Number(id),
          marketplaceIntegrationDatoId: integrationCodes.a3innuva,
        }
      : currentPayrollRun?.id
      ? {
          companyId: company.id,
          id: currentPayrollRun.id,
          marketplaceIntegrationDatoId: integrationCodes.a3innuva,
        }
      : undefined

  const { data, loading, error } = useCompensationsPayrollRunQuery({
    skip: isCyclesManagementFeatureEnabled ? !id : !currentPayrollRun?.id,
    variables: queryVariables,
  })

  const mainLegalEntityCurrencySymbol =
    data?.apiCore.company?.legalEntities.find((el) => el.main)?.currency ?? EUR.symbol
  const legalEntityCurrencySymbol =
    data?.compensations.payrollRunsConnection.nodes[0]?.cycle?.currency || undefined

  useCurrencyUpdate({
    mainLegalEntityCurrencySymbol,
    legalEntityCurrencySymbol,
    isCyclesManagementFeatureEnabled,
  })

  const [fetchPayrollResults, { data: payrollResultsData }] = useLazyQuery(
    CompensationsPayrollResultsDocument,
    {
      fetchPolicy: 'network-only',
    }
  )

  const payrollRun = data?.compensations?.payrollRunsConnection.nodes[0]

  useUnsafeEffect(() => {
    const payrollRunId = payrollRun?.id
    if (!payrollRunId) return

    setContextParameters({ payroll_run_id: payrollRunId.toString() })
    refreshPayrollResults()
  }, [payrollRun, setContextParameters])

  const refreshPayrollResults = () => {
    if (!payrollRun) return

    fetchPayrollResults({
      variables: {
        payrollRunIds: [payrollRun.id],
        orderBy: { field: 'createdAt', order: 'DESC' },
      },
    })
  }

  const hasWebsockets = data?.features?.featuresConnection?.nodes[0]?.enabled ?? false

  const [exportOpened, setExportOpened] = React.useState<boolean>()
  const [showTogglePayrollRunLockModal, setShowTogglePayrollRunLockModal] =
    React.useState<boolean>(false)
  const [showManagePayrollResultsModal, setShowManagePayrollResultsModal] =
    React.useState<boolean>(false)
  const [openLockTooltip, toggleLockTooltip] = useToggle(false)
  const [showExportAlertModal, setShowExportAlertModal] = React.useState(false)

  const pendingToDefineCount = data?.compensations?.pendingToDefine?.totalCount ?? 0
  const aboveLimitCount = data?.compensations?.aboveLimit?.totalCount ?? 0
  const hasPendingValidations = pendingToDefineCount > 0 || aboveLimitCount > 0

  const handleExportClick = () => {
    if (hasPendingValidations) {
      setShowExportAlertModal(true)
    } else {
      setExportOpened(true)
      track(trackingEvents.compensations.openExportSidePanel)
    }
  }

  const handleConfirmExport = () => {
    setShowExportAlertModal(false)
    setExportOpened(true)
    track(trackingEvents.compensations.openExportSidePanel)
  }

  const handleCancelExport = () => {
    setShowExportAlertModal(false)
  }

  const handleRefresh = () => {
    if (!payrollRun) return

    refreshPayrollResults()

    client.refetchQueries({
      include: ['CompensationsListColumns', 'ValidationTypeFilters', 'EmployeePayrollRuns'],
    })
  }

  if (error || errorFeatures) {
    throw new Error('Error fetching data')
  }

  if (loading || payrollRunsLoading || loadingFeatures) {
    return (
      <Box padding="s40" width="full" alignItems="center">
        <Spinner />
      </Box>
    )
  }

  if (!payrollRun) {
    return null
  }

  const payrollResults = payrollResultsData?.payroll?.resultsConnection?.nodes ?? []

  const earningsConceptsCategories = [
    'earnings_benefits_in_kind',
    'earnings_fixed_salary',
    'earnings_others',
    'earnings_variable',
  ]

  let payrollConcepts = data?.payroll?.conceptsConnection.nodes ?? []

  if (payrollResults.length === 0) {
    payrollConcepts = payrollConcepts.filter(
      (payrollConcept) =>
        payrollConcept.name !== DEFAULT_RESULT_CONCEPT.base_salary &&
        payrollConcept.category &&
        earningsConceptsCategories.includes(payrollConcept.category)
    )
  }

  const availablePayrollConcepts = [...payrollConcepts].sort((a, b) =>
    a.label.localeCompare(b.label)
  )

  const canToggleLock: boolean =
    data?.compensations?._policies?.payrollRunToggleLockStatus?.can ?? false
  const canExportCompensations: boolean =
    data?.compensations?._policies?.employeeCompensationExport?.can ?? false

  const endsOn = stringToDate(payrollRun?.endsOn, 'backend_datetime')
  const lastDayOfMonth = moment().endOf('month').startOf('day')
  const isPastPeriod = endsOn.isBefore(lastDayOfMonth, 'day')
  const isClosed =
    PayrollRunStatusEnum.Closed === payrollRun.status ||
    PayrollRunStatusEnum.Completed === payrollRun.status
  const lockedAtDate = payrollRun.lockedAt
    ? stringToDate(payrollRun.lockedAt, 'backend_datetime')
    : null

  const canImportCompensations =
    data.universalImporter?._policies.importResources.can &&
    data.compensations?._policies?.employeeCompensationBulkImport?.can &&
    payrollRun?.id !== null &&
    payrollRun?.id !== undefined

  const canImportResults =
    data.compensations?._policies?.payrollResultImport?.can &&
    payrollRun?.id !== null &&
    payrollRun?.id !== undefined

  const showImportResults = Boolean(canImportCompensations && canImportResults)

  // Keep type reference to avoid orphan checks
  // TODO: Remove this once we have a proper way to test the ImportPayrollResultModal in
  // Finance workspace in the next PRs
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  type _UnusedImportPayrollResultModal = typeof ImportPayrollResultModal

  const handleManagePayrollResultsClick = () => {
    setShowManagePayrollResultsModal(true)
    track(trackingEvents.compensations.clickManagePayrollResults)
  }

  return (
    <CompensationsPayrollRunLayout
      onActionClick={(key) => {
        switch (key) {
          case 'lock-payroll-run':
            setShowTogglePayrollRunLockModal(true)
            break
        }
      }}
    >
      <PayrollRunEmptyStateCallout payrollRun={payrollRun} />
      <PayrollRunStatusAlert
        isClosed={isPastPeriod || isClosed}
        lockedAt={lockedAtDate}
        canToggleLock={canToggleLock}
      />
      {payrollRun && hasWebsockets && (
        <PayrollRunDataUpdatedInfo payrollRunId={payrollRun.id} onRefresh={handleRefresh} />
      )}
      <Box alignSelf="flexEnd">
        <Box flexDirection="row" gap="s12" alignSelf="flexEnd">
          {!isCyclesManagementFeatureEnabled && payrollRun && canToggleLock && (
            <LockToggleButton
              isClosed={isPastPeriod || isClosed}
              isLocked={!!payrollRun.lockedAt}
              onToggle={() => setShowTogglePayrollRunLockModal(true)}
              openLockTooltip={openLockTooltip}
              toggleLockTooltip={toggleLockTooltip}
            />
          )}
          {!isCyclesManagementFeatureEnabled && data && (
            <PayrollRunActions
              data={data}
              payrollResults={payrollResults}
              onExportClick={handleExportClick}
              onManagePayrollResultsClick={handleManagePayrollResultsClick}
              canExportCompensations={canExportCompensations}
              isClosed={isPastPeriod || isClosed}
              isCyclesManagementFeatureEnabled={isCyclesManagementFeatureEnabled}
            />
          )}
        </Box>
      </Box>
      {showExportAlertModal && (
        <CompensationsExportAlertModal
          pendingToDefineCount={pendingToDefineCount}
          aboveLimitCount={aboveLimitCount}
          onSuccess={handleConfirmExport}
          onClose={handleCancelExport}
        />
      )}
      {exportOpened && (
        <ExportSidepanel
          resource="compensations.payroll_run_employees_compensation"
          overrideFilters={{ payroll_run_id: [payrollRun.id] }}
          metadata={{ payroll_run_id: [payrollRun.id] }}
          hasDynamicFields
          onClose={() => {
            setExportOpened(false)
          }}
          onExport={() => {
            if (!!!payrollRun?.lockedAt && !isClosed) {
              setShowTogglePayrollRunLockModal(true)
            }
            track(trackingEvents.compensations.completeExport, {
              payroll_run_id: payrollRun.id,
              payroll_run_locked_at: payrollRun.lockedAt,
            })
          }}
          fieldsTitleTooltip={i18n.t(
            'compensation.payroll_run_employees_compensation.exportSidePanel.header_column_tooltip'
          )}
        />
      )}
      {payrollRun && (
        <PayrollRunCompensationsList
          payrollRun={payrollRun}
          availablePayrollConcepts={availablePayrollConcepts}
          locale={locale}
          canToggleLock={canToggleLock}
          isCyclesManagementFeatureEnabled={isCyclesManagementFeatureEnabled}
          onOpenExportSidePanel={handleExportClick}
          // TODO: we need to refactor passing down all of this handlers. This is going to be tackled once we make the clean up and remove the FF
          onManagePayrollResultsClick={() => setShowManagePayrollResultsModal(true)}
          payrollResults={payrollResults}
          showImportResults={showImportResults}
        />
      )}

      {payrollRun && showTogglePayrollRunLockModal && (
        <TogglePayrollRunLockModal
          payrollRun={payrollRun}
          onSuccess={() => {
            setShowTogglePayrollRunLockModal(false)
          }}
          onClose={() => setShowTogglePayrollRunLockModal(false)}
          lock={!!!payrollRun.lockedAt}
        />
      )}
      {payrollRun && showManagePayrollResultsModal && (
        <ManagePayrollResultsModal
          payrollResults={payrollResults}
          onUpdate={() => {
            refreshPayrollResults()
          }}
          onClose={() => {
            setShowManagePayrollResultsModal(false)
            refreshPayrollResults()
          }}
          onImportNewResultClick={() => {
            setShowManagePayrollResultsModal(false)
            navigate(
              navigation.compensations.definition.importResult
                .fullPath()
                .replace(/\*+$/, '')
                .concat(`?payroll-run-id=${payrollRun.id}`)
            )
          }}
          isCyclesManagementFeatureEnabled={isCyclesManagementFeatureEnabled}
        />
      )}
      <SyncFooter />
    </CompensationsPayrollRunLayout>
  )
}

type PayrollRunCompensationsListProps = {
  payrollRun: CompensationsListFragment
  availablePayrollConcepts?: Array<AvailablePayrollConceptFieldFragment>
  locale: string
  canToggleLock?: boolean
  isCyclesManagementFeatureEnabled: boolean
  onOpenExportSidePanel: () => void
  onManagePayrollResultsClick: () => void
  payrollResults: PayrollResultFragment[]
  showImportResults: boolean
}

function PayrollRunCompensationsList({
  payrollRun,
  availablePayrollConcepts,
  locale,
  canToggleLock,
  isCyclesManagementFeatureEnabled,
  onOpenExportSidePanel,
  onManagePayrollResultsClick,
  payrollResults,
  showImportResults,
}: PayrollRunCompensationsListProps) {
  const { isEditing, onClose, onEdit, editValue } = useCrudDisclosure<EmployeePayrollRunNode>()

  const refreshFnRef = React.useRef<(() => void) | null>(null)

  const refreshCompensations = React.useCallback(() => {
    if (refreshFnRef.current) {
      refreshFnRef.current()
    }
  }, [])

  const isClosed =
    PayrollRunStatusEnum.Closed === payrollRun.status ||
    PayrollRunStatusEnum.Completed === payrollRun.status
  const readonly = (!!payrollRun.lockedAt && !canToggleLock) || isClosed

  return (
    <CompensationsRefreshContext.Provider value={{ refreshCompensations }}>
      <Box flexDirection="column" style={{ height: '100%' }}>
        {!isCyclesManagementFeatureEnabled ? (
          <CompensationsList
            payrollRun={payrollRun}
            onEdit={onEdit}
            locale={locale}
            refreshFnRef={refreshFnRef}
          />
        ) : (
          <CycleCompensationsList
            payrollRun={payrollRun}
            onEdit={onEdit}
            locale={locale}
            onOpenExportSidePanel={onOpenExportSidePanel}
            onManagePayrollResultsClick={onManagePayrollResultsClick}
            payrollResults={payrollResults}
            showImportResults={showImportResults}
            refreshFnRef={refreshFnRef}
          />
        )}

        <Sidepanel
          open={isEditing}
          title={
            isEditing && editValue
              ? `${i18n.t('compensations.PayrollRunCompensationsList.titleCompensationsOf')} ${
                  editValue?.employee?.firstName
                } ${editValue?.employee?.lastName}`
              : null
          }
          onClose={onClose}
          closeLabel="Close"
          width="wide"
          boxPadding={false}
        >
          {isEditing && editValue?.employee?.id && availablePayrollConcepts && (
            <EditCompensationsFormStandalone
              availablePayrollConcepts={availablePayrollConcepts}
              employeeId={editValue.employee.id}
              payrollRunId={payrollRun.id}
              readonly={readonly}
            />
          )}
        </Sidepanel>
      </Box>
    </CompensationsRefreshContext.Provider>
  )
}

export { CompensationsPayrollRun }
